/**
 * Storage utilities for Supabase Storage
 * Handles image uploads using the existing 'products' bucket
 */

import { supabase } from './supabase';

const STORAGE_BUCKET = 'products';
const PRODUCT_IMAGES_PATH = 'product-images';
const CATEGORY_IMAGES_PATH = 'category-images';
const LOCATION_IMAGES_PATH = 'location-images';
const LOGO_PATH = 'logo';

const MAX_DIMENSION = 1280;
const MAX_FILE_SIZE_BYTES = 300 * 1024; // 300KB
const INITIAL_QUALITY = 0.85;
const MIN_QUALITY = 0.3;

/**
 * Compress and resize an image file using canvas.
 * Resizes to fit within MAX_DIMENSION, then iteratively lowers JPEG quality
 * until the result is under MAX_FILE_SIZE_BYTES.
 */
export async function compressImage(file: File): Promise<File> {
  // Skip non-raster formats (SVG doesn't benefit from canvas compression)
  if (file.type === 'image/svg+xml') return file;
  // Already small enough — no compression needed
  if (file.size <= MAX_FILE_SIZE_BYTES) return file;

  const bitmap = await createImageBitmap(file);
  const { width, height } = bitmap;

  // Calculate new dimensions, preserving aspect ratio
  let newWidth = width;
  let newHeight = height;
  if (width > MAX_DIMENSION || height > MAX_DIMENSION) {
    const ratio = Math.min(MAX_DIMENSION / width, MAX_DIMENSION / height);
    newWidth = Math.round(width * ratio);
    newHeight = Math.round(height * ratio);
  }

  const canvas = new OffscreenCanvas(newWidth, newHeight);
  const ctx = canvas.getContext('2d')!;
  ctx.drawImage(bitmap, 0, 0, newWidth, newHeight);
  bitmap.close();

  // For PNG with transparency, try PNG first then fall back to JPEG
  const outputType = file.type === 'image/png' ? 'image/png' : 'image/jpeg';

  // If PNG, try a single pass — if still too large, convert to JPEG
  if (outputType === 'image/png') {
    const pngBlob = await canvas.convertToBlob({ type: 'image/png' });
    if (pngBlob.size <= MAX_FILE_SIZE_BYTES) {
      return new File([pngBlob], file.name, { type: 'image/png' });
    }
    // PNG too large — fall through to JPEG compression
  }

  // Iteratively reduce JPEG quality until under target size
  let quality = INITIAL_QUALITY;
  let blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });

  while (blob.size > MAX_FILE_SIZE_BYTES && quality > MIN_QUALITY) {
    quality -= 0.05;
    blob = await canvas.convertToBlob({ type: 'image/jpeg', quality });
  }

  const compressedName = file.name.replace(/\.[^.]+$/, '.jpg');
  return new File([blob], compressedName, { type: 'image/jpeg' });
}

/**
 * Upload a product image to Supabase Storage
 *
 * @param file - The image file to upload
 * @param merchantId - The merchant ID for organizing files
 * @returns The public URL of the uploaded image
 */
export async function uploadProductImage(
  file: File,
  merchantId: string
): Promise<string> {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload an image file');
  }

  // Validate file size (10MB)
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File size must be less than 10MB');
  }

  // Compress image
  const compressed = await compressImage(file);

  // Create unique filename with product-images subdirectory
  const fileExt = compressed.name.split('.').pop();
  const fileName = `${PRODUCT_IMAGES_PATH}/${merchantId}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

  // Upload to Supabase Storage
  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(fileName, compressed, {
      cacheControl: '3600',
      upsert: false
    });

  if (uploadError) {
    console.error('Error uploading file:', uploadError);
    throw new Error(`Failed to upload image: ${uploadError.message}`);
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Upload a location image to Supabase Storage
 *
 * @param file - The image file to upload
 * @param merchantId - The merchant ID for organizing files
 * @returns The public URL of the uploaded image
 */
export async function uploadLocationImage(
  file: File,
  merchantId: string
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload an image file');
  }

  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File size must be less than 10MB');
  }

  const compressed = await compressImage(file);

  const fileExt = compressed.name.split('.').pop();
  const fileName = `${PRODUCT_IMAGES_PATH}/${merchantId}/${LOCATION_IMAGES_PATH}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(fileName, compressed, {
      cacheControl: '3600',
      upsert: false
    });

  if (uploadError) {
    console.error('Error uploading file:', uploadError);
    throw new Error(`Failed to upload image: ${uploadError.message}`);
  }

  const { data: { publicUrl } } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Upload a category image to Supabase Storage
 *
 * @param file - The image file to upload
 * @param merchantId - The merchant ID for organizing files
 * @returns The public URL of the uploaded image
 */
export async function uploadCategoryImage(
  file: File,
  merchantId: string
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload an image file');
  }

  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File size must be less than 10MB');
  }

  const compressed = await compressImage(file);

  const fileExt = compressed.name.split('.').pop();
  const fileName = `${PRODUCT_IMAGES_PATH}/${merchantId}/${CATEGORY_IMAGES_PATH}/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(fileName, compressed, {
      cacheControl: '3600',
      upsert: false
    });

  if (uploadError) {
    console.error('Error uploading file:', uploadError);
    throw new Error(`Failed to upload image: ${uploadError.message}`);
  }

  const { data: { publicUrl } } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Delete a product image from Supabase Storage
 *
 * @param imageUrl - The public URL of the image to delete
 * @returns True if deletion was successful
 */
export async function deleteProductImage(imageUrl: string): Promise<boolean> {
  try {
    // Extract file path from URL
    const urlParts = imageUrl.split(`/${STORAGE_BUCKET}/`);
    if (urlParts.length < 2) {
      console.error('Invalid image URL format');
      return false;
    }

    const filePath = urlParts[1];

    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([filePath]);

    if (error) {
      console.error('Error deleting image:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteProductImage:', error);
    return false;
  }
}

/**
 * Upload an email asset (logo or image) to Supabase Storage
 *
 * @param file - The image file to upload
 * @param merchantId - The merchant ID for organizing files
 * @param assetType - Type of asset ('logo' or 'image')
 * @returns The public URL of the uploaded asset
 */
export async function uploadEmailAsset(
  file: File,
  merchantId: string,
  assetType: 'logo' | 'image'
): Promise<string> {
  // Validate file type
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload an image file');
  }

  // Validate file size (10MB)
  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File size must be less than 10MB');
  }

  // Compress image
  const compressed = await compressImage(file);

  // Create unique filename within product-images path (same bucket path as products for RLS compatibility)
  const fileExt = compressed.name.split('.').pop();
  const fileName = `${PRODUCT_IMAGES_PATH}/${merchantId}/email-${assetType}s/${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

  // Upload to Supabase Storage
  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(fileName, compressed, {
      cacheControl: '3600',
      upsert: false
    });

  if (uploadError) {
    console.error('Error uploading file:', uploadError);
    throw new Error(`Failed to upload ${assetType}: ${uploadError.message}`);
  }

  // Get public URL
  const { data: { publicUrl } } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(fileName);

  return publicUrl;
}

/**
 * Delete an email asset from Supabase Storage
 *
 * @param imageUrl - The public URL of the asset to delete
 * @returns True if deletion was successful
 */
/**
 * Upload a merchant logo or favicon to Supabase Storage
 *
 * @param file - The image file to upload
 * @param merchantId - The merchant ID for organizing files
 * @param assetType - Type of asset ('logo' or 'favicon')
 * @returns The public URL of the uploaded asset
 */
export async function uploadMerchantBrandAsset(
  file: File,
  merchantId: string,
  assetType: 'logo' | 'favicon'
): Promise<string> {
  if (!file.type.startsWith('image/')) {
    throw new Error('Please upload an image file');
  }

  if (file.size > 10 * 1024 * 1024) {
    throw new Error('File size must be less than 10MB');
  }

  // Compress image
  const compressed = await compressImage(file);

  const fileExt = compressed.name.split('.').pop();
  const fileName = `${PRODUCT_IMAGES_PATH}/${merchantId}/${LOGO_PATH}/${assetType}-${Date.now()}-${Math.random().toString(36).substring(7)}.${fileExt}`;

  const { error: uploadError } = await supabase.storage
    .from(STORAGE_BUCKET)
    .upload(fileName, compressed, {
      cacheControl: '3600',
      upsert: false
    });

  if (uploadError) {
    console.error('Error uploading file:', uploadError);
    throw new Error(`Failed to upload ${assetType}: ${uploadError.message}`);
  }

  const { data: { publicUrl } } = supabase.storage
    .from(STORAGE_BUCKET)
    .getPublicUrl(fileName);

  return publicUrl;
}

export async function deleteEmailAsset(imageUrl: string): Promise<boolean> {
  try {
    // Extract file path from URL
    const urlParts = imageUrl.split(`/${STORAGE_BUCKET}/`);
    if (urlParts.length < 2) {
      console.error('Invalid asset URL format');
      return false;
    }

    const filePath = urlParts[1];

    const { error } = await supabase.storage
      .from(STORAGE_BUCKET)
      .remove([filePath]);

    if (error) {
      console.error('Error deleting asset:', error);
      return false;
    }

    return true;
  } catch (error) {
    console.error('Error in deleteEmailAsset:', error);
    return false;
  }
}
